import {onSnapshot} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import type {CourseId, UserId} from 'types/common';
import {SECTION_TEACH_ATTEMPTS_ENFORCED} from '../sectionTeachingFlow';
import {
  getSectionTeachingAttemptSummaryRef,
  type SectionTeachingAttemptSummary,
} from './sectionTeachingAttempt';

/**
 * Live view of the applicant's own section-teaching attempt summary, written
 * only by the callables. Lets the green room show remaining attempts, refuse a
 * start once exhausted (`SectionPreSessionView`), and show the chosen final
 * attempt before anything is consumed.
 * Exhaustion requires both numbers to be present, so the client never guesses
 * the server's default cap, and the callable stays the authority at allocation
 * time. When {@link SECTION_TEACH_ATTEMPTS_ENFORCED} is `false`, `isExhausted`
 * is always `false` (attempt limiting is disabled). A read failure clears the
 * preview rather than blocking the flow.
 *
 * @param userId - The applicant whose summary to subscribe to; when undefined
 *   the hook resets to `null`.
 * @param courseId - The target course; when undefined the hook resets to `null`.
 * @param options.demo - `true` subscribes to the staff demo collection
 *   (`demoTeachAttempts`) instead of the applicant one. Omitted → the real
 *   applicant collection.
 * @return `{summary, isExhausted}` — the raw summary (or null) and whether the
 *   attempt cap has been reached.
 */
export const useSectionTeachingAttemptSummary = (
  userId: string | undefined,
  courseId: CourseId | undefined,
  options?: {demo?: boolean},
) => {
  const [summary, setSummary] =
    useState<SectionTeachingAttemptSummary | null>(null);

  // A primitive dependency, so callers may pass an inline options object
  // without resubscribing every render.
  const demo = options?.demo === true;

  useEffect(() => {
    if (!userId || courseId === undefined) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- reset while the applicant or course is unknown
      setSummary(null);
      return;
    }
    const unsubscribe = onSnapshot(
      getSectionTeachingAttemptSummaryRef(userId as UserId, courseId, {demo}),
      snap => setSummary(snap.data() ?? null),
      () => setSummary(null),
    );
    return unsubscribe;
  }, [userId, courseId, demo]);

  const isExhausted =
    SECTION_TEACH_ATTEMPTS_ENFORCED &&
    typeof summary?.attemptCount === 'number' &&
    typeof summary?.maxAttempts === 'number' &&
    summary.attemptCount >= summary.maxAttempts;

  return {summary, isExhausted};
};
